<template>
    <div class="row p-2">
        <div class="row align-items-center">
            <div class="col-5">
                <input v-model.trim="search" type="text" class="form-control form-control-sm search-input"
                    placeholder="🔍 Search...." @keyup="onSearch()" />
            </div>

            <div class="filter-options-wrapper justify-content-center col-7 d-flex mt-2 mb-2">
                <div class="dropdown d-flex flex-row align-items-center">
                    <button type="button" class="fw-bold btn btn-outline-info btn-sm me-4 filter-button"
                        data-bs-toggle="dropdown">
                        <i class="filter-icon fa-solid fa-filter"></i>
                        <span class="filter-text"> Filter </span>
                    </button>

                    <div class="dropdown-menu p-2 filter-dropdown-menu">
                        <div class="d-flex flex-row align-items-center justify-content-between">
                            <select v-model="filters.type" class="form-select shadow-none" style="width:150px;">
                                <option value="">None</option>
                                <option value="organisation">Organisation</option>
                                <option value="product">Product</option>
                            </select>
                            <button type="button" class="btn btn-warning text-light btn-sm ms-2 small"
                                style="width: 80px" @click.stop="clearFilter()">Clear
                            </button>
                        </div>

                        <div v-if="filters.type" class="p-2 d-flex flex-row align-items-center">
                            <select v-model="filters.value" class="form-select shadow-none w-100">
                                <option :value="null">All</option>
                                <option v-for="option in filterValueOptions" :key="option.id" :value="option.id">
                                    {{ option.name }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="d-flex flex-row align-items-center">
                    <span class="p-2 filter-title">
                        <span class="filter-text">Sort:</span>
                    </span>
                    <select v-model="sorting" class="form-select shadow-none filter-options">
                        <option v-for="option in sortByList" :key="option.text" :value="option.value">
                            {{ option.text }}
                        </option>
                    </select>
                </div>
            </div>
        </div>
    </div>

    <div v-if="isLoading" class="text-center p-5">
        <p>Loading...</p>
    </div>

    <div v-else-if="!testimonials.length" class="testimonial-card-no-rec">
        <p>No records found.</p>
    </div>

    <div v-else class="mt-3 mb-2 testimonial-list row row-cols-auto row-cols-sm-2">
        <div v-for="testimonial in testimonials" :key="testimonial.id" class="col-12 mt-2 mb-2 align-items-stretch">
            <div v-if="testimonial.testimonial" class="card h-100 testimonial-card">
                <div class="card-header">
                    <img v-if="testimonial.testimonial_image?.full_link"
                        class="fill" :src="testimonial.testimonial_image.full_link" :alt="`${testimonial.contact_name} testimonial`">
                    <img v-else class="cover" src="@/assets/img/no_image.png" alt="No image available">
                </div>
                <div class="card-body m-2">
                    <div class="card-body-height">
                        <div class="testimonial">
                            <p  :title="testimonial.testimonial" class="testimonial-text">"{{ truncateLongText(testimonial.testimonial, 20) }}"</p>
                            <p class="testimonial-author">{{ testimonial.contact_name }}</p>
                            <p class="testimonial-title">{{ testimonial.contact_role }} - {{
                                organisationMap[testimonial.organisation_id] || 'Unknown Organisation' }}</p>
                        </div>
                        <br>
                        <div class="d-row d-flex flex-row-reverse align-items-start">
                            <button v-if="!$store.state.auth.isGeneralUser" type="button"
                                class="btn btn-outline-info btn-circle ms-1" title="Add to Content Block"
                                @click.stop="addToContent(testimonial)">
                                <i class="fa-solid fa-plus"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row text-center pt-3 pb-3">
        <div class="d-inline pagination">
            <a @click="previousPage()" class="pagination-arrow" :class="{ 'disabled': pagination.page === 1 }">
                <i class="fa fa-chevron-left"></i>
            </a>
            <span class="pagination-label">
                <strong>{{ paginationLabel }} of {{ pagination.totalItems }}</strong>
            </span>
            <a @click="nextPage()" class="pagination-arrow" :class="{ 'disabled': isLastPage }">
                <i class="fa fa-chevron-right"></i>
            </a>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { debounce, truncateLongText } from '@/plugins/helper.js'

export default {
    props: {
        organisationId: String,
        testimonialID: [String, Number]
    },

    data() {
        return {
            isLoading: false,
            search: "",
            debounceFn: null,
            testimonials: [],
            organisations: [],
            productList: [],
            pagination: {
                pageSize: 4,
                page: 1,
                totalItems: 0,
                totalPages: 1,
            },
            // --- REFACTORED STATE ---
            // 1. Simplified sorting state. `v-model` now binds directly to this object.
            sorting: {
                field: "dateCreated",
                order: "desc"
            },
            // 2. Simplified filtering state into a single object.
            filters: {
                type: "", // 'organisation' or 'product'
                value: null // The ID of the organisation/product
            },
            // 3. Made `sortByList` more structured to avoid string parsing.
            sortByList: [
                { text: "Date: Newest", value: { field: "dateCreated", order: "desc" } },
                { text: "Date: Oldest", value: { field: "dateCreated", order: "asc" } },
            ],
        }
    },

    computed: {
        // --- PERFORMANCE OPTIMIZATION ---
        // Create a lookup map for organisation names. This is much faster than
        // calling a `find()` method inside a v-for loop.
        organisationMap() {
            return this.organisations.reduce((map, org) => {
                map[org.id] = org.name;
                return map;
            }, {});
        },

        // --- DYNAMIC FILTER OPTIONS ---
        // This computed property now dynamically provides the options for the filter value dropdown.
        filterValueOptions() {
            switch (this.filters.type) {
                case "organisation":
                    return this.organisations.map(org => ({ id: org.id, name: org.name }));
                case "product":
                    return this.productList.map(p => ({ id: p.id, name: p.display_name }));
                default:
                    return [];
            }
        },

        paginationLabel() {
            const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
            const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
            return `${start} - ${end}`;
        },

        isLastPage() {
            return this.pagination.page >= this.pagination.totalPages || this.pagination.totalItems === 0;
        }
    },

    watch: {
        // --- STREAMLINED WATCHERS ---
        // Watch the entire pagination object or specific properties that trigger a fetch.
        'pagination.page': 'getTestimonialList',
        'pagination.pageSize': 'resetPageAndFetch',
        
        // Watch the whole sorting object. Any change to it will trigger a fetch.
        sorting: {
            handler: 'getTestimonialList',
            deep: true
        },
        
        // When the filter *type* changes (e.g., from 'organisation' to 'product'),
        // reset the selected filter *value*.
        'filters.type'() {
            this.filters.value = null;
            this.getTestimonialList();
        },
        
        // When the filter *value* changes, fetch the new data.
        'filters.value': 'getTestimonialList',

        // Watch for external changes to organisationId prop
        organisationId: 'init'
    },

    created() {
        this.init();
        this.debounceFn = debounce(() => {
             this.pagination.page = 1;
             this.getTestimonialList();
        }, 500);
    },

    methods: {
        truncateLongText, // Make helper function available in template
        
        async init() {
            this.isLoading = true;
            // Use Promise.all to fetch initial data concurrently for faster load times.
            await Promise.all([
                this.getAuthOrganisations(),
                this.getProductList()
            ]);
            await this.getTestimonialList();
            this.isLoading = false;
        },

        formatDate(date) {
            return moment(date).format("DD-MMM-YYYY");
        },

        previousPage() {
            if (this.pagination.page > 1) {
                this.pagination.page--;
            }
        },

        nextPage() {
            if (!this.isLastPage) {
                this.pagination.page++;
            }
        },
        
        resetPageAndFetch() {
            this.pagination.page = 1;
            this.getTestimonialList();
        },

        onSearch() {
            this.debounceFn();
        },

        clearFilter() {
            this.filters.type = "";
            this.filters.value = null;
            // getTestimonialList() will be triggered by the watcher on filters.type
        },

        addToContent(testimonial) {
            const testimonial_link = testimonial.testimonial_image?.full_link || null;
            this.$parent.addTestimonialToContent(testimonial, testimonial_link);
        },
        
        // --- REFACTORED API CALL ---
        async getTestimonialList() {
            this.isLoading = true;

            // Use Axios's `params` object for cleaner, safer query string generation.
            const params = {
                pageSize: this.pagination.pageSize,
                page: this.pagination.page,
                sort: this.sorting.field,
                order: this.sorting.order,
            };

            if (this.search) {
                params.search = this.search;
            }
            
            // Add filter parameters if they are set
            if (this.filters.type && this.filters.value) {
                params[this.filters.type] = this.filters.value;
            }

            try {
                const response = await this.$axios.get("/testimonials/text", { params });
                this.testimonials = response.data.testimonials;
                this.pagination.totalItems = response.data.total_items;
                this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize);
            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Failed to fetch testimonials.");
                console.error("Error fetching testimonials:", error);
            } finally {
                this.isLoading = false;
            }
        },

        async getAuthOrganisations() {
            try {
                const response = await this.$axios_auth.get("/vertex/organisations");
                this.organisations = response.data.organisations;
            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Failed to fetch organisations.");
            }
        },

        async getProductList() {
            try {
                const response = await this.$axios.get("/categories?name=Products");
                this.productList = response.data.category.subcategories.map(p => ({
                    id: p.id,
                    display_name: p.display_name
                }));
            } catch (error) {
                this.$toast.error(error.response?.data?.message || "Failed to fetch products.");
                console.error(error);
            }
        },
    },
}
</script>

<style scoped>
/* Move inline styles to this block for better organization */
.search-input {
    border-radius: 40px;
    font-size: 14px;
    font-family: 'Roboto', sans-serif;
}

.filter-button {
    width: 100%;
    min-width: 75px;
    border-radius: 10px !important;
}

.filter-dropdown-menu {
    min-width: 250px;
}

.filter-options {
    width: auto;
    border: none;
}

.testimonial-list {
    display: flex;
    flex-wrap: wrap;
}

.testimonial-card {
    border-radius: 9px;
    box-shadow: 0px 10px 25px 0px rgba(0, 0, 0, 0.10);
}

.testimonial-card .card-header {
    padding: 0;
    border-bottom: none;
    height: 80px;
    background-color: whitesmoke;
    border-radius: 10px 10px 0 0;
}

.testimonial-card-no-rec {
    display: flex;
    margin: 1rem;
    justify-content: center;
    font-weight: 900;
    font-size: 18px;
}

.fill {
    object-fit: fill;
    width: 100%;
    height: 100%;
    border-radius: 10px 10px 0 0;
}

.cover {
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: 10px 10px 0 0;
}

.card-body-height {
    min-height: 80px;
}

.testimonial {
    font-family: sans-serif;
    padding: 0; /* Padding is on the card-body */
}

.testimonial-text {
    font-size: 16px;
    line-height: 1.5;
}

.testimonial-author {
    font-weight: bold;
    margin-top: 10px;
}

.testimonial-title {
    font-size: 14px;
    color: #00A0C1;
}

.pagination-arrow {
    margin: 0 8px;
    color: #00a0c1;
    cursor: pointer;
}

.pagination-arrow.disabled {
    color: #ccc;
    cursor: not-allowed;
}

.pagination-label {
    display: inline-block;
}
</style>